/**
 * PermissionsTab — Admin > Permissions (ADR-0046, migration 0157).
 *
 * The owner-only matrix over per-tenant role-capability overrides. Every
 * cell is a toggle reflecting the EFFECTIVE value (default ± override);
 * overridden cells carry a visible marker + a per-cell "Revert to
 * default"; locked cells (the owner column, plus write_demo_clinical and
 * manage_permissions) render inert with the reason on hover/focus.
 *
 * No confirm dialogs — every toggle applies immediately (optimistic UI via
 * the mutation's own pending state), then reconciles with the server and
 * pushes a house UndoToast entry (undo fires the inverse route). Turning
 * `read` off for a role is a SOFT rail: an inline warning shows, the
 * change still applies (JC-5 — never a fourth hard-lock).
 *
 * Desktop (md+): one card per capability domain, a table with role
 * columns; wrapped in its own overflow-x-auto (never the page body).
 * Mobile: a role-selector pill row, one role's capabilities at a time as
 * stacked rows — the matrix never fits mobile width meaningfully.
 *
 * Above the matrix sits the tenant's client record setting
 * (ClientRecordAccessCard): who can open client records, the width the
 * engagement lane reads at.
 *
 * Precedents mirrored: AiLimitsTab.tsx (effective-value resolution +
 * mutation + toast shape), AdminReviewTabs.tsx (section/pill pattern).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  permissionsApi,
  type PermissionCellState,
  type PermissionRoleColumn,
  type PermissionsMatrixResponse,
} from '@/lib/permissions';
import { errorCodeOf, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { PERMISSION_GROUPS, capabilityLabel, capabilityBlurb, roleLabel, roleBlurb } from '@/lib/permissionCopy';
import type { Capability } from '@/lib/capabilities';
import { useTenant } from '@/lib/auth';
import { useUndo } from '@/lib/undo';
import { Button } from '@/components/primitives/Button';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { ClientRecordAccessCard } from './ClientRecordAccessCard';
import { SelectField } from '@/components/primitives/SelectField';

export interface PermissionsTabProps {
  tenantSlug: string;
}

const QUERY_KEY = (tenantSlug: string): readonly unknown[] => [
  'admin-review',
  'permissions',
  tenantSlug,
];

function findEntry(roles: PermissionRoleColumn[], role: string, cap: string): PermissionCellState | null {
  const col = roles.find((r) => r.role === role);
  return col?.entries.find((e) => e.capability === cap) ?? null;
}

/** A refused permission write, as one sentence: a locked cell says so;
 *  every other failure is the house save sentence. */
function permissionWriteErrorText(e: unknown, contact: SupportContact): string {
  if (errorCodeOf(e) === 'locked_cell') {
    return "That permission is fixed, so nothing changed. You can change the others on this screen.";
  }
  return pageWriteText(e, contact);
}

export function PermissionsTab({ tenantSlug }: PermissionsTabProps): ReactNode {
  const { refreshWhoami } = useTenant();
  const contact = useSupportContact();
  const { push: pushUndo } = useUndo();
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEY(tenantSlug);

  const query = useQuery<PermissionsMatrixResponse, Error>({
    queryKey,
    queryFn: ({ signal }) => permissionsApi.list(tenantSlug, signal),
    staleTime: 15_000,
  });

  const [warning, setWarning] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pendingCell, setPendingCell] = useState<string | null>(null);
  const [mobileRole, setMobileRole] = useState<string | null>(null);

  const roles = query.data?.roles ?? [];

  const activeMobileRole = mobileRole ?? roles.find((r) => !r.locked)?.role ?? roles[0]?.role ?? null;

  async function refetch(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey });
  }

  const setMutation = useMutation({
    mutationFn: (args: { role: string; capability: string; enabled: boolean }) =>
      permissionsApi.setOverride(tenantSlug, args.role, args.capability, args.enabled),
  });
  const revertMutation = useMutation({
    mutationFn: (args: { role: string; capability: string }) =>
      permissionsApi.revert(tenantSlug, args.role, args.capability),
  });

  async function toggleCell(role: string, capability: string, entry: PermissionCellState): Promise<void> {
    if (entry.locked) return;
    const cellKey = `${role} ${capability}`;
    setErrorMsg(null);
    setPendingCell(cellKey);

    const priorEffective = entry.effective;
    const priorOverridden = entry.overridden;
    const nextEnabled = !priorEffective;

    if (capability === 'read' && nextEnabled === false) {
      setWarning(`Turning this off means people with the ${roleLabel(role)} role won't be able to open the app.`);
    } else {
      setWarning(null);
    }

    try {
      await setMutation.mutateAsync({ role, capability, enabled: nextEnabled });
      await refetch();
      await refreshWhoami?.();

      const label = `${roleLabel(role)} can ${nextEnabled ? 'now' : 'no longer'} ${capabilityLabel(capability as Capability).toLowerCase()}.`;
      pushUndo({
        label,
        undo: async () => {
          if (priorOverridden) {
            await permissionsApi.setOverride(tenantSlug, role, capability, priorEffective);
          } else {
            await permissionsApi.revert(tenantSlug, role, capability);
          }
          await refetch();
          await refreshWhoami?.();
        },
      });
    } catch (e) {
      setErrorMsg(permissionWriteErrorText(e, contact));
    } finally {
      setPendingCell(null);
    }
  }

  async function revertCell(role: string, capability: string, entry: PermissionCellState): Promise<void> {
    if (entry.locked || !entry.overridden) return;
    const cellKey = `${role} ${capability}`;
    setErrorMsg(null);
    setPendingCell(cellKey);

    const priorEffective = entry.effective;

    try {
      const result = await revertMutation.mutateAsync({ role, capability });
      await refetch();
      await refreshWhoami?.();

      pushUndo({
        label: `${roleLabel(role)}’s ${capabilityLabel(capability as Capability).toLowerCase()} setting reverted to default.`,
        undo: async () => {
          await permissionsApi.setOverride(tenantSlug, role, capability, priorEffective);
          await refetch();
          await refreshWhoami?.();
        },
      });
      void result;
    } catch (e) {
      setErrorMsg(permissionWriteErrorText(e, contact));
    } finally {
      setPendingCell(null);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="font-display text-lg text-sage-800">Permissions</h3>
        <p className="text-xs text-ink-soft mt-1 max-w-prose">
          Changes take effect right away; menus update the next time a person opens or refreshes the
          page.
        </p>
      </div>

      <ClientRecordAccessCard tenantSlug={tenantSlug} />

      {warning && (
        <div
          role="alert"
          className="mb-3 px-3 py-2 text-sm bg-warm-50 border border-warm-300 text-ink rounded-md"
        >
          {warning}
        </div>
      )}

      {errorMsg && (
        <div
          role="alert"
          className="mb-3 px-3 py-2 text-sm bg-warm-50 border border-warm-300 text-ink rounded-md"
          data-testid="permissions-write-error"
        >
          {errorMsg}
        </div>
      )}

      {query.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the permissions" />}

      {query.isSuccess && roles.length > 0 && (
        <>
          {/* Mobile: role selector + one role's capabilities, stacked. */}
          <div className="md:hidden">
            <label htmlFor="permissions-role-select" className="sr-only">
              Role
            </label>
            <SelectField size="default"
              id="permissions-role-select"
              value={activeMobileRole ?? ''}
              onChange={(e) => setMobileRole(e.target.value)}
              className="w-full mb-4"
            >
              {roles.map((r) => (
                <option key={r.role} value={r.role}>
                  {roleLabel(r.role)} · {r.people_count}
                </option>
              ))}
            </SelectField>

            {activeMobileRole &&
              PERMISSION_GROUPS.map((group) => {
                const col = roles.find((r) => r.role === activeMobileRole);
                if (!col) return null;
                const entries = group.capabilities
                  .map((cap) => col.entries.find((e) => e.capability === cap))
                  .filter((e): e is PermissionCellState => e !== undefined);
                if (entries.length === 0) return null;
                return (
                  <section key={group.title} className="mb-4 bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                    <h4 className="px-3 pt-3 font-display text-base text-sage-800">{group.title}</h4>
                    <div className="divide-y divide-sage-800/[6%]">
                      {entries.map((entry) => (
                        <MobileCapabilityRow
                          key={entry.capability}
                          entry={entry}
                          pending={pendingCell === `${activeMobileRole} ${entry.capability}`}
                          onToggle={() => toggleCell(activeMobileRole, entry.capability, entry)}
                          onRevert={() => revertCell(activeMobileRole, entry.capability, entry)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
          </div>

          {/* Desktop/tablet: one card per domain, roles as columns. */}
          <div className="hidden md:block">
            {PERMISSION_GROUPS.map((group) => (
              <PermissionGroupCard
                key={group.title}
                title={group.title}
                capabilities={group.capabilities}
                roles={roles}
                pendingCell={pendingCell}
                onToggle={toggleCell}
                onRevert={revertCell}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PermissionGroupCard({
  title,
  capabilities,
  roles,
  pendingCell,
  onToggle,
  onRevert,
}: {
  title: string;
  capabilities: readonly Capability[];
  roles: PermissionRoleColumn[];
  pendingCell: string | null;
  onToggle: (role: string, cap: Capability, entry: PermissionCellState) => void;
  onRevert: (role: string, cap: Capability, entry: PermissionCellState) => void;
}): ReactNode {
  const rows = useMemo(
    () => capabilities.filter((cap) => roles.some((r) => findEntry(roles, r.role, cap) !== null)),
    [capabilities, roles],
  );
  if (rows.length === 0) return null;

  return (
    <div className="mb-5 bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 overflow-x-auto">
      <h4 className="px-4 pt-4 font-display text-lg text-sage-800">{title}</h4>
      <table className="w-full text-sm mt-2">
        <thead>
          <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
            <th className="px-4 py-2 font-medium min-w-[220px]">Permission</th>
            {/* Founder ruling 2026-08-27: every CAPABILITY row in this matrix
                carries a one-line blurb, but the role columns carried only a
                label and a head count — so the question the matrix exists to
                answer ("what is this seat for?") went unanswered at the top
                of every column. The blurb wraps rather than forcing the
                column wider; the label and the count keep their nowrap.

                The blurb is text-xs, matching the capabilityBlurb below
                and PeopleTab's roleBlurb(). Scale steps only, never an
                arbitrary px size: px ignores the reader's text-size
                setting and 200% zoom is a launch requirement
                (tools/lint-type-scale.sh enforces it). */}
            {roles.map((col) => (
              <th key={col.role} className="px-3 py-2 font-medium text-center align-top">
                <span className="block whitespace-nowrap">{roleLabel(col.role)}</span>
                <span className="block text-[11px] font-normal text-ink-soft/80 whitespace-nowrap">
                  {col.people_count} {col.people_count === 1 ? 'person' : 'people'}
                </span>
                <span className="block max-w-[150px] mx-auto mt-1 text-xs font-normal leading-snug text-ink-soft/80">
                  {roleBlurb(col.role)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cap) => (
            <tr key={cap} className="border-b border-sage-800/[5%] last:border-0 align-top">
              <td className="px-4 py-3">
                <div className="text-ink font-medium">{capabilityLabel(cap)}</div>
                <div className="text-xs text-ink-soft mt-0.5 max-w-xs">{capabilityBlurb(cap)}</div>
              </td>
              {roles.map((col) => {
                const entry = findEntry(roles, col.role, cap);
                if (!entry) return <td key={col.role} className="px-3 py-3 text-center">&ndash;</td>;
                const cellKey = `${col.role} ${cap}`;
                return (
                  <td key={col.role} className="px-3 py-3 text-center">
                    <PermissionCellControl
                      entry={entry}
                      pending={pendingCell === cellKey}
                      onToggle={() => onToggle(col.role, cap, entry)}
                      onRevert={() => onRevert(col.role, cap, entry)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PermissionCellControl({
  entry,
  pending,
  onToggle,
  onRevert,
}: {
  entry: PermissionCellState;
  pending: boolean;
  onToggle: () => void;
  onRevert: () => void;
}): ReactNode {
  return (
    <div className="inline-flex flex-col items-center gap-1">
      <Button
        type="button"
        role="switch"
        aria-checked={entry.effective}
        aria-label={capabilityLabel(entry.capability as Capability)}
        variant={entry.effective ? 'secondary' : 'ghost'}
        size="sm"
        disabled={entry.locked || pending}
        title={entry.locked ? entry.locked_reason : undefined}
        onClick={onToggle}
        className={
          entry.effective
            ? 'bg-sage-100 text-sage-700 border-sage-300'
            : 'bg-warm-50 text-ink-soft border-warm-300'
        }
      >
        {entry.effective ? 'On' : 'Off'}
      </Button>
      {entry.overridden && !entry.locked && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRevert}
          disabled={pending}
          className="!min-h-0 h-auto px-0 py-0 text-[10px] uppercase tracking-wide underline decoration-dotted"
        >
          Changed · Revert
        </Button>
      )}
    </div>
  );
}

function MobileCapabilityRow({
  entry,
  pending,
  onToggle,
  onRevert,
}: {
  entry: PermissionCellState;
  pending: boolean;
  onToggle: () => void;
  onRevert: () => void;
}): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm text-ink">{capabilityLabel(entry.capability as Capability)}</div>
        <div className="text-xs text-ink-soft mt-0.5">{capabilityBlurb(entry.capability as Capability)}</div>
        {entry.overridden && !entry.locked && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRevert}
            disabled={pending}
            className="!min-h-0 h-auto mt-1 px-0 py-0 text-[10px] uppercase tracking-wide underline decoration-dotted"
          >
            Changed · Revert to default
          </Button>
        )}
      </div>
      <Button
        type="button"
        role="switch"
        aria-checked={entry.effective}
        aria-label={capabilityLabel(entry.capability as Capability)}
        variant={entry.effective ? 'secondary' : 'ghost'}
        size="sm"
        disabled={entry.locked || pending}
        title={entry.locked ? entry.locked_reason : undefined}
        onClick={onToggle}
        className={
          (entry.effective
            ? 'bg-sage-100 text-sage-700 border-sage-300'
            : 'bg-warm-50 text-ink-soft border-warm-300') + ' shrink-0'
        }
      >
        {entry.effective ? 'On' : 'Off'}
      </Button>
    </div>
  );
}
